import { useToast } from "@chakra-ui/react";
import React, { useMemo } from "react";

import { successToast } from "../../../../components";
import {
  AiInterviewCallFragment,
  CallNoteFragment,
  CallNoteType,
  TranscriptSegmentFragment,
  useCallShareQuery,
} from "../../../graphql";
import useFeatureFlag from "../../../graphql/hooks/useFeatureFlag";
import { VideoDisplayMode } from "../../../types";
import { extractIntegrityTimelineEvents } from "../../AiRecording/IntegritySignalCard/utils";
import {
  CallbackRef,
  MediaListeners,
  MediaPlayerInterface,
} from "../../MediaPlayer/useMediaPlayer";
import {
  VideoCallbackRef,
  VideoStateControl,
} from "../../MediaPlayer/useVideoState";
import { MediaPlayerV2 } from "../../MediaPlayerV2/MediaPlayerV2";
import useAddNote from "../useAddNote";

type AiInterviewMediaPlayerProps = {
  call: Pick<
    AiInterviewCallFragment,
    | "id"
    | "speakers"
    | "duration"
    | "canEdit"
    | "candidateCameraTimestamps"
    | "aiInterview"
    | "startTime"
  >;
  notes: CallNoteFragment[];
  segments: TranscriptSegmentFragment[];
  // Media player props
  mediaPlayerRef: CallbackRef;
  listeners: MediaListeners;
  mediaSrc?: string;
  onSeek(t: number): void;
  onCreateClipClick?(): void;
  player: MediaPlayerInterface;
  videoDisplayMode: VideoDisplayMode;
  // Video-specific props
  isVideoVisible: boolean;
  videoRef: VideoCallbackRef;
  hideVideo(enablePIP?: boolean): void;
  showVideo?(): void;
  fullScreenControls: VideoStateControl;
  pipControls: VideoStateControl;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
};

export const AiInterviewMediaPlayer: React.FC<AiInterviewMediaPlayerProps> = ({
  call,
  notes,
  segments,
  mediaPlayerRef,
  listeners,
  mediaSrc,
  onSeek,
  onCreateClipClick,
  player,
  videoDisplayMode,
  isVideoVisible,
  videoRef,
  hideVideo,
  showVideo,
  fullScreenControls,
  pipControls,
  containerRef,
}) => {
  const toast = useToast();
  const signalsEnabled = useFeatureFlag(
    "ai_interviewer:interview_integrity:ui"
  );
  const addNote = useAddNote({
    callId: call.id,
    onCompleted: (data) => {
      const note = data.addNewCallNote?.callNote;
      if (note) {
        successToast(
          toast,
          `${note.type === CallNoteType.Note ? "Note" : "Reaction"} added`,
          { position: "top" }
        );
      }
    },
  });

  const shareQuery = useCallShareQuery({ variables: { id: call.id } });

  const integrityEvents = useMemo(() => {
    if (!signalsEnabled || !call.aiInterview?.integritySignals) return [];
    return extractIntegrityTimelineEvents(
      call.aiInterview.integritySignals,
      call.startTime
    );
  }, [signalsEnabled, call.aiInterview?.integritySignals, call.startTime]);

  return (
    <MediaPlayerV2
      mediaPlayerRef={mediaPlayerRef}
      listeners={listeners}
      mediaSrc={mediaSrc}
      onSeek={onSeek}
      onCreateClipClick={onCreateClipClick}
      player={player}
      containerRef={containerRef}
      videoDisplayMode={videoDisplayMode}
      isVideoVisible={isVideoVisible}
      videoRef={videoRef}
      hideVideo={hideVideo}
      showVideo={showVideo}
      fullScreenControls={fullScreenControls}
      pipControls={pipControls}
      callId={call.id}
      speakers={call.speakers}
      canChangeSpeaker={false}
      canExpandSpeakerBars
      duration={call.duration}
      onAddNote={addNote}
      notes={notes}
      cameraTimestamps={call.candidateCameraTimestamps}
      integrityEvents={integrityEvents}
      segments={segments}
      shareableUsers={shareQuery.data?.call?.shareableUsers}
      shareableUsersLoading={shareQuery.loading}
    />
  );
};
